import { useEffect, useMemo, useState } from 'react';
import { ImagePlus, Loader2, Trash2 } from 'lucide-react';
import { apiPost, getToken } from '@/shared/api/client';
import { Button } from '@/components/ui/Button';

const API_BASE = import.meta.env.VITE_API_BASE ?? '';

type UploadEntity = 'equipment' | 'user' | 'item_model' | 'kit_template' | 'item' | 'kit' | 'maintenance' | 'recall' | 'bio_indicator' | 'batch';
type UploadKind = 'PHOTO' | 'EVIDENCE' | 'MANUAL' | 'REPORT' | 'BIOMETRIC';

interface PresignResult {
  upload_url: string;
  method: 'PUT';
  headers: { 'content-type': string };
  key: string;
  expires_at: string;
}

interface ImageUploadFieldProps {
  label: string;
  hint?: string;
  value: string;
  onChange: (next: string) => void;
  entity: UploadEntity;
  entityId?: string;
  kind?: UploadKind;
  required?: boolean;
}

export function ImageUploadField({
  label,
  hint,
  value,
  onChange,
  entity,
  entityId,
  kind = 'PHOTO',
  required,
}: ImageUploadFieldProps) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const key = useMemo(() => (value || '').trim(), [value]);

  useEffect(() => {
    let revoked = false;
    let objectUrl: string | null = null;

    async function loadPreview() {
      if (!key) {
        setPreviewUrl(null);
        return;
      }
      try {
        const token = getToken();
        const res = await fetch(`${API_BASE}/v1/uploads/get?key=${encodeURIComponent(key)}`, {
          headers: { Authorization: `Bearer ${token ?? ''}` },
        });
        if (!res.ok) throw new Error('Falha ao carregar imagem');
        const blob = await res.blob();
        objectUrl = URL.createObjectURL(blob);
        if (!revoked) setPreviewUrl(objectUrl);
      } catch {
        if (!revoked) setPreviewUrl(null);
      }
    }

    loadPreview();
    return () => {
      revoked = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [key]);

  async function onFileSelected(file: File | null) {
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      const presign = await apiPost<PresignResult>('/v1/uploads/presign', {
        entity,
        entity_id: entityId || 'draft',
        kind,
        content_type: file.type || 'application/octet-stream',
      });

      const uploadRes = await fetch(`${API_BASE}${presign.upload_url}`, {
        method: 'PUT',
        headers: { 'content-type': file.type || 'application/octet-stream' },
        body: file,
      });
      if (!uploadRes.ok) {
        const text = await uploadRes.text().catch(() => '');
        throw new Error(text || `Falha no upload (${uploadRes.status})`);
      }
      onChange(presign.key);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex flex-col gap-1.5 sm:col-span-2">
      <label className="text-xs font-medium text-neutral-700 dark:text-neutral-300">
        {label}{required ? ' *' : ''}
      </label>
      <div className="rounded border border-neutral-300 bg-neutral-0 p-3 dark:border-neutral-700 dark:bg-neutral-900">
        <div className="mb-3 flex items-center gap-2">
          <label className="ict-focus-ring inline-flex cursor-pointer items-center gap-2 rounded border border-neutral-300 px-3 py-2 text-sm hover:bg-neutral-100 dark:border-neutral-700 dark:hover:bg-neutral-800">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />}
            Selecionar imagem
            <input
              type="file"
              accept="image/png,image/jpeg,image/webp"
              className="hidden"
              onChange={(e) => onFileSelected(e.target.files?.[0] ?? null)}
              disabled={busy}
            />
          </label>
          <Button
            size="sm"
            variant="ghost"
            leadingIcon={<Trash2 className="h-4 w-4" />}
            onClick={() => onChange('')}
            disabled={!value || busy}
          >
            Remover
          </Button>
        </div>

        {previewUrl ? (
          <img src={previewUrl} alt="Preview" className="h-32 w-32 rounded border border-neutral-300 object-cover dark:border-neutral-700" />
        ) : (
          <div className="grid h-32 w-32 place-items-center rounded border border-dashed border-neutral-300 text-xs text-neutral-500 dark:border-neutral-700">
            Sem imagem
          </div>
        )}

        {value ? <p className="mt-2 truncate font-mono text-xxs text-neutral-500">{value}</p> : null}
        {hint ? <p className="mt-2 text-xs text-neutral-500">{hint}</p> : null}
        {error ? <p className="mt-2 text-xs text-danger">{error}</p> : null}
      </div>
    </div>
  );
}
